import { useCallback } from 'react';

import { Nullable } from '@rbi-ctg/frontend';
import { ICartEntry, IItem } from '@rbi-ctg/menu';
import {
  IAddOnSection,
  IAddOnSectionOption,
  IItemAcceptingFreeOffer,
  IItemsAcceptingFreeOffers,
  IServiceModes,
} from 'hooks/use-feature-menu-add-on';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { ServiceMode } from 'state/service-mode';

export const useAddOnsItemContainer = () => {
  const { serviceMode, removeAllFromCart, updateQuantity } = useOrderContext();
  const enableFreeAddonOnCartFeatureFlag = useFlag(LaunchDarklyFlag.ENABLE_FREE_ADDON_ON_CART);

  const isServiceValid = useCallback(
    (sectionServices: IServiceModes) => {
      const serviceModeMap = {
        [ServiceMode.TAKEOUT]: 'pickUpServiceMode',
        [ServiceMode.EAT_IN]: 'dineInServiceMode',
        [ServiceMode.CURBSIDE]: 'curbsideServiceMode',
        [ServiceMode.DRIVE_THRU]: 'driveThruServiceMode',
        [ServiceMode.TABLE_SERVICE]: 'tableServiceMode',
        [ServiceMode.DELIVERY]: 'deliveryServiceMode',
        [ServiceMode.CATERING_DELIVERY]: 'cateringDeliveryServiceMode',
        [ServiceMode.CATERING_PICKUP]: 'cateringPickUpServiceMode',
      };

      const correspondingProperty = serviceModeMap[serviceMode];
      const isValid = !!sectionServices[correspondingProperty];

      return isValid;
    },
    [serviceMode]
  );

  const removeList = useCallback(
    (cartList: ICartEntry[], sectionList: IAddOnSection[]) => {
      const removeAddons = cartList.filter(cartItem => {
        return sectionList.some(sectionItem => {
          const hasItemsInCart = sectionItem.options?.some(
            opt =>
              opt.option._id === cartItem._id ||
              (enableFreeAddonOnCartFeatureFlag && opt.freeOption?._id === cartItem._id)
          );
          const isServiceInvalid =
            sectionItem.showSectionItemsOnCart &&
            !isServiceValid(sectionItem.sectionItemsServiceModes);

          return hasItemsInCart && isServiceInvalid;
        });
      });

      removeAllFromCart(removeAddons);
    },
    [enableFreeAddonOnCartFeatureFlag, isServiceValid, removeAllFromCart]
  );

  const countMaximumFreeOffer = useCallback(
    (
      cartEntries: ICartEntry[],
      itemsAcceptingFreeOffers: IItemsAcceptingFreeOffers[] | undefined
    ): number => {
      if (!itemsAcceptingFreeOffers?.length) {
        return 0;
      }

      const maximumFreeOfferQuantity = itemsAcceptingFreeOffers.reduce(
        (sum: number, { itemAcceptingFreeOffer, maxFreeQuantity }: IItemsAcceptingFreeOffers) => {
          const matchingItem = cartEntries.find(
            (entry: ICartEntry) => entry._id === itemAcceptingFreeOffer._id
          );

          return matchingItem ? sum + (maxFreeQuantity * matchingItem?.quantity || 0) : sum;
        },
        0
      );

      return maximumFreeOfferQuantity;
    },
    []
  );

  const showPaidItemForFreeInSection = useCallback(
    (
      cartEntries: ICartEntry[],
      freeOption: Nullable<IItem>,
      itemsAcceptingFreeOffers: Nullable<IItemsAcceptingFreeOffers[]>
    ): boolean => {
      if (!itemsAcceptingFreeOffers) {
        return false;
      }

      const freeItemInCart = cartEntries.find(entry => entry._id === freeOption?._id);

      const isMaximumFreeSelected =
        freeItemInCart?.quantity === countMaximumFreeOffer(cartEntries, itemsAcceptingFreeOffers);

      return isMaximumFreeSelected;
    },
    [countMaximumFreeOffer]
  );

  const addOnHasOffer = useCallback(
    (cartEntry: ICartEntry[], itemsAcceptingFreeOffers: Nullable<IItemsAcceptingFreeOffers[]>) => {
      const isInEntries = (entry: ICartEntry) =>
        itemsAcceptingFreeOffers?.some(
          ({ itemAcceptingFreeOffer }) => entry._id === itemAcceptingFreeOffer._id
        );

      return cartEntry.some(isInEntries);
    },
    []
  );

  const freeAddonsIdsList = useCallback(
    (options: IAddOnSectionOption[], entries: ICartEntry[]): string[] => {
      const optionsThatHasOfferOnEntries = options.filter(({ itemsAcceptingFreeOffers }) =>
        addOnHasOffer(entries, itemsAcceptingFreeOffers)
      );

      const freeIdsList = optionsThatHasOfferOnEntries.reduce(
        (acc: string[], option: IAddOnSectionOption) => {
          if (option.freeOption) {
            acc.push(option.freeOption._id);
          }

          return acc;
        },
        []
      );

      return freeIdsList;
    },
    [addOnHasOffer]
  );

  const isAllFreeAddonsInCart = useCallback(
    (entries: ICartEntry[], options: IAddOnSectionOption[]): boolean => {
      const idsList = freeAddonsIdsList(options, entries);

      const isInEntries = (freeAddonId: string) => entries.some(item => item._id === freeAddonId);

      return idsList.every(isInEntries);
    },
    [freeAddonsIdsList]
  );

  const existsAllAddonsInCart = useCallback(
    (section: IAddOnSection, entries: ICartEntry[]): boolean => {
      const { showSectionItemsOnCart, options } = section;

      const isInEntries = (sectionOption: IAddOnSectionOption) => {
        return entries.some(item => {
          const isOptionInEntries = item._id === sectionOption.option._id;
          const paidAddonIsNotOnSection =
            section.enableAddonAsFreeItem &&
            enableFreeAddonOnCartFeatureFlag &&
            !showPaidItemForFreeInSection(
              entries,
              sectionOption.freeOption,
              sectionOption.itemsAcceptingFreeOffers
            );

          return isOptionInEntries || paidAddonIsNotOnSection;
        });
      };

      const allOptionsExist = options.every(isInEntries);

      const isAllFreeInCart =
        !enableFreeAddonOnCartFeatureFlag || !section.enableAddonAsFreeItem
          ? true
          : isAllFreeAddonsInCart(entries, options);

      return showSectionItemsOnCart && allOptionsExist && isAllFreeInCart;
    },
    [enableFreeAddonOnCartFeatureFlag, isAllFreeAddonsInCart, showPaidItemForFreeInSection]
  );

  const isItemsAcceptingFreeOffer = useCallback(
    (section: IAddOnSection, cartEntries: ICartEntry[]): boolean => {
      const { enableAddonAsFreeItem, options } = section;
      if (!enableFreeAddonOnCartFeatureFlag || !enableAddonAsFreeItem) {
        return true;
      }

      const isInEntries = (
        itemAcceptingFreeOffer: IItemAcceptingFreeOffer,
        entries: ICartEntry[]
      ) => entries.some(entry => itemAcceptingFreeOffer._id === entry._id);

      const hasOptionsWithFreeOffers = options.some(({ itemsAcceptingFreeOffers }) =>
        itemsAcceptingFreeOffers?.some(({ itemAcceptingFreeOffer }) =>
          isInEntries(itemAcceptingFreeOffer, cartEntries)
        )
      );

      return hasOptionsWithFreeOffers;
    },
    [enableFreeAddonOnCartFeatureFlag]
  );

  const updateFreeAndOptionQuantity = useCallback(
    (freeOptionInCart: ICartEntry, optionInCart: ICartEntry, maximumOfFreeOffer: number) => {
      const { quantity: optionQuantity, cartId: optionCartId } = optionInCart;
      const { quantity: freeOptionQuantity, cartId: freeOptionCartId } = freeOptionInCart;

      const freePlusOptionQuantity = freeOptionQuantity + optionQuantity;
      const updatedFreeQuantity =
        freePlusOptionQuantity > maximumOfFreeOffer ? maximumOfFreeOffer : freePlusOptionQuantity;

      const freeOptionQuantityDifference = updatedFreeQuantity - freeOptionQuantity;
      const updatedOptionQuantity = optionQuantity - freeOptionQuantityDifference;

      const optionItemReference = updatedOptionQuantity < 1 ? optionInCart : optionCartId;

      updateQuantity(optionItemReference, updatedOptionQuantity);
      updateQuantity(freeOptionCartId, updatedFreeQuantity);
    },
    [updateQuantity]
  );

  const updateItemsQuantity = useCallback(
    (optionsWithOfferInCart: IAddOnSectionOption[], entries: ICartEntry[]) => {
      optionsWithOfferInCart.forEach(({ itemsAcceptingFreeOffers, freeOption, option }) => {
        const maximumOfFreeOffer = countMaximumFreeOffer(entries, itemsAcceptingFreeOffers!);

        const freeAddonInCart = entries.find(entry => entry._id === freeOption?._id);

        const optionInCart = entries.find(entry => entry._id === option._id);

        const isNotMaximumOfFreeSelected =
          freeAddonInCart && freeAddonInCart.quantity === maximumOfFreeOffer;

        const isFreeQuantityBiggerThanMaximum =
          freeAddonInCart && freeAddonInCart.quantity > maximumOfFreeOffer;

        if (!isNotMaximumOfFreeSelected && freeAddonInCart && optionInCart) {
          updateFreeAndOptionQuantity(freeAddonInCart, optionInCart, maximumOfFreeOffer);
        } else if (freeAddonInCart && isFreeQuantityBiggerThanMaximum) {
          updateQuantity(freeAddonInCart?.cartId, maximumOfFreeOffer);
        }
      });
    },
    [countMaximumFreeOffer, updateFreeAndOptionQuantity, updateQuantity]
  );

  const freeItemsWithOfferActive = useCallback(
    (addOnSections: IAddOnSection[], cartEntry: ICartEntry[]) => {
      const itemsWithOfferActive = addOnSections.reduce(
        (allItemsAcceptingOffer: IItem[], sections) => {
          const { options, enableAddonAsFreeItem } = sections;

          options.forEach(({ itemsAcceptingFreeOffers, freeOption }) => {
            if (!enableAddonAsFreeItem) {
              return;
            }

            if (
              freeOption &&
              itemsAcceptingFreeOffers?.length &&
              addOnHasOffer(cartEntry, itemsAcceptingFreeOffers)
            ) {
              allItemsAcceptingOffer.push(freeOption);
            }
          });

          return allItemsAcceptingOffer;
        },
        []
      );

      return itemsWithOfferActive;
    },
    [addOnHasOffer]
  );

  const optionsWithOffersInCart = useCallback(
    (cartEntry: ICartEntry[], addOnSections: IAddOnSection[]) => {
      const optionsWithFreeOffer = addOnSections
        .filter(section => {
          const { showSectionItemsOnCart, enableAddonAsFreeItem } = section;

          const isAddonAsFreeEnabled = showSectionItemsOnCart && enableAddonAsFreeItem;

          if (!isAddonAsFreeEnabled) {
            return false;
          }

          return isItemsAcceptingFreeOffer(section, cartEntry);
        })
        .flatMap(({ options }) => options);

      return optionsWithFreeOffer.filter(({ freeOption }) =>
        cartEntry.some(entry => entry._id === freeOption?._id)
      );
    },
    [isItemsAcceptingFreeOffer]
  );

  const optionsInCartWithoutOffer = useCallback(
    (cartEntry: ICartEntry[], addOnSections: IAddOnSection[]) => {
      const optionsWithoutFreeOffer = addOnSections
        .filter(section => {
          const { showSectionItemsOnCart, enableAddonAsFreeItem } = section;

          const isAddonAsFreeEnabled = showSectionItemsOnCart && enableAddonAsFreeItem;

          return isAddonAsFreeEnabled && !isItemsAcceptingFreeOffer(section, cartEntry);
        })
        .flatMap(({ options }) => options);

      return optionsWithoutFreeOffer.filter(({ freeOption }) => {
        const itemsWithOfferActive = freeItemsWithOfferActive(addOnSections, cartEntry);

        return !itemsWithOfferActive.some(item => item._id === freeOption?._id);
      });
    },
    [freeItemsWithOfferActive, isItemsAcceptingFreeOffer]
  );

  const removeItemsWithoutFreeOfferInCart = useCallback(
    (cartEntry: ICartEntry[], addOnSections: IAddOnSection[]) => {
      const options = optionsInCartWithoutOffer(cartEntry, addOnSections);

      options.forEach(({ freeOption, option }) => {
        const freeAddonInCart = cartEntry.find(entry => entry._id === freeOption?._id);

        const optionInCart = cartEntry.find(entry => entry._id === option._id);

        if (freeAddonInCart) {
          updateQuantity(freeAddonInCart, 0);
        }

        if (optionInCart) {
          updateQuantity(optionInCart, 0);
        }
      });
    },
    [optionsInCartWithoutOffer, updateQuantity]
  );

  const checkAndAdjustItems = useCallback(
    (cartEntry: ICartEntry[], addOnSections: IAddOnSection[]): void => {
      const optionsWithOfferAplied = optionsWithOffersInCart(cartEntry, addOnSections);

      if (optionsWithOfferAplied.length) {
        updateItemsQuantity(optionsWithOfferAplied, cartEntry);
      }

      removeItemsWithoutFreeOfferInCart(cartEntry, addOnSections);
    },
    [optionsWithOffersInCart, removeItemsWithoutFreeOfferInCart, updateItemsQuantity]
  );

  return {
    isServiceValid,
    removeList,
    existsAllAddonsInCart,
    showPaidItemForFreeInSection,
    countMaximumFreeOffer,
    freeAddonsIdsList,
    isAllFreeAddonsInCart,
    isItemsAcceptingFreeOffer,
    addOnHasOffer,
    checkAndAdjustItems,
  };
};
